import React, { useEffect, useMemo, useState } from "react";
import AppSearchBar from "../ui/store-seach-bar";
import classNames from "classnames";
import useDebounce from "../../hooks/useDebounce";
import { GetStoreItems } from "../../../api/items";
import { GetStoreItemsParams } from "@/api/interfaces/items.interface";
import { useRequest } from "@/api/utils";
import { ProductItemInterface } from "@/assets/interfaces";
import ProductItem from "../items/product-item";
import ProductItemLoader from "../items/product-item-loader";
import { computeItemAvailability } from "@/assets/js/utils/utils";
import { useRouter } from "next/router";
import useLockPageScroll from "../../hooks/useLockPageScroll";
import StorefrontContext from "@/contexts/storefront";

interface MiniSearchProps {
  toggle: () => void;
  show: boolean;
  storeId?: string;
  storeSlug: string;
  openItem: (item: ProductItemInterface) => void;
}

const MiniSearch: React.FC<MiniSearchProps> = ({ toggle, show, storeId, openItem, storeSlug }) => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ProductItemInterface[]>([]);
  const { paths } = StorefrontContext.useContainer();

  const { response, error: itemsFetchError, isLoading, makeRequest } = useRequest<GetStoreItemsParams>(GetStoreItems);

  useLockPageScroll(show);
  const debouncedSearch = useDebounce(searchQuery, 500, async (debouncedSearchQuery) => {
    if (!debouncedSearchQuery || !show) {
      setSearchResults([]);
      return;
    }

    const filter = {
      store: storeId,
      search: debouncedSearchQuery,
    };

    const [response, error] = await makeRequest({
      filter,
      separateFeaturedItems: false,
      page: 1,
      per_page: 4,
    });

    if (response?.data?.items?.other_items?.length > 0) {
      setSearchResults(response?.data?.items?.other_items);
    }
  });

  const formattedSearchResults = useMemo(() => {
    return searchResults.map((i) => computeItemAvailability(i));
  }, [searchResults]);

  useEffect(() => {
    if (show) {
      setSearchQuery("");
    }

    const handleRouteChange = (url: string, { shallow }: { shallow: boolean }) => {
      if (url.includes("/products")) {
        setSearchResults([]);
        if (show) {
          toggle();
        }
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [show]);

  return (
    <>
      <div
        className={classNames(
          "bg-white fixed top-0 sm:absolute sm:top-full left-0 w-full z-[999] transition-all duration-300 ease-out origin-top",
          show ? "h-auto py-5 pb-20 sm:pb-8" : "max-h-0 overflow-hidden"
        )}
      >
        <div className="w-full">
          <div className="store-container flex flex-col px-5 sm:px-15 md:px-25">
            <div className="flex items-center justify-between w-full">
              <div className={classNames("flex-1", show ? "opacity-100" : "opacity-0 pointer-events-none")}>
                <AppSearchBar
                  {...{
                    placeholder: `Find Products`,
                    searchQuery,
                    setSearchQuery,
                    resetPage: () => {},
                    useSearchParams: false,
                    inputVisible: show,
                  }}
                />
              </div>
              <button className="text-sm ml-2.5 font-medium text-black-muted" onClick={() => toggle()}>
                Close
              </button>
            </div>
            {debouncedSearch && (
              <div className="border-t border-grey-divider w-full mt-5 pt-5">
                <div className="w-full">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-4 md:gap-7.5 lg:gap-10">
                    {!isLoading &&
                      formattedSearchResults.map((item, index) => (
                        <ProductItem index={index} key={item.id} openItem={() => openItem(item)} item={item} />
                      ))}

                    {isLoading && Array.from(Array(4).keys()).map((_, index) => <ProductItemLoader key={index} />)}
                  </div>
                  {!isLoading && searchResults.length < 1 && debouncedSearch && (
                    <div className="w-full flex items-center justify-center">
                      <figure className="bg-grey-fields-100 w-6 h-6 rounded-full mr-2 flex items-center justify-center bg-primary-500 bg-opacity-10">
                        {/* prettier-ignore */}
                        <svg width="55%" viewBox="0 0 24 24" fill="none" className="text-primary-500">
                        <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M21.0004 20.9999L16.6504 16.6499" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      </figure>
                      <p className="text-sm text-black-muted">No results found for &quot;{debouncedSearch}&quot;</p>
                    </div>
                  )}
                </div>

                {searchResults.length > 0 && !isLoading && (
                  <button
                    onClick={() => router.push(`${paths.products}?search=${debouncedSearch}`)}
                    className="gap-1.5 items-center text-sm sm:text-1sm font-semibold md:text-base flex text-primary-500 mt-5"
                  >
                    See All Results ({response?.total}){/* prettier-ignore */}
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M3.75 9H14.25M14.25 9L9 3.75M14.25 9L9 14.25" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={classNames(
          "absolute top-full w-full left-0 bg-black z-[998] transition-all duration-300 ease-out origin-top h-screen",
          show ? "opacity-70" : "opacity-0 pointer-events-none"
        )}
        onClick={toggle}
      ></div>
    </>
  );
};

export default MiniSearch;
